import { forwardRef, useContext, useState, type ComponentPropsWithoutRef } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";

import { cn, platformProps } from "../../lib";
import { AccordionItemContext, AccordionRootContext } from "./hooks";
import { useWarnIfEmptyItemValue } from "./item-value";
import { useTriggerDisabled } from "./trigger-disabled";
import type {
  AccordionContentPrimitiveProps,
  AccordionHeaderPrimitiveProps,
  AccordionItemPrimitiveProps,
  AccordionRootContextValue,
  AccordionRootPrimitiveProps,
  AccordionTriggerPrimitiveProps,
} from "./types";

/** Ref types, resolved per platform like the components. */
export type AccordionRootRef = HTMLDivElement;
export type AccordionItemRef = HTMLDivElement;
export type AccordionHeaderRef = HTMLHeadingElement;
export type AccordionTriggerRef = HTMLButtonElement;
export type AccordionContentRef = HTMLDivElement;

type RootValue = string | string[];

/**
 * Accordion Root — web (Radix), unstyled. Styling lives in the molecule layer.
 *
 * Mirrors the open value for `useAccordionRootContext` / `useAccordionItemContext`:
 * the prop when controlled, otherwise every value Radix reports. Radix itself
 * stays uncontrolled when `value` is omitted. Switching `type` starts over from
 * `defaultValue`, as Radix's own single and multiple implementations do.
 */
const AccordionRoot = forwardRef<AccordionRootRef, AccordionRootPrimitiveProps>((props, ref) => (
  <MirroredRoot key={props.type} ref={ref} {...props} />
));

const MirroredRoot = forwardRef<AccordionRootRef, AccordionRootPrimitiveProps>(
  ({ className, children, testID, ...rest }, ref) => {
    const empty: RootValue = rest.type === "single" ? "" : [];
    const [reported, setReported] = useState<RootValue>(rest.defaultValue ?? empty);
    const value = rest.value ?? reported;
    const onValueChange = (next: RootValue) => {
      setReported(next);
      (rest.onValueChange as ((value: RootValue) => void) | undefined)?.(next);
    };
    const context = {
      type: rest.type,
      value,
      disabled: rest.disabled,
    } as AccordionRootContextValue;

    return (
      <AccordionRootContext.Provider value={context}>
        <AccordionPrimitive.Root
          ref={ref}
          className={className}
          {...platformProps({ testID, slot: "accordion" })}
          {...(rest as ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>)}
          onValueChange={onValueChange as never}
        >
          {children}
        </AccordionPrimitive.Root>
      </AccordionRootContext.Provider>
    );
  },
);

MirroredRoot.displayName = "AccordionMirroredRoot";
AccordionRoot.displayName = AccordionPrimitive.Root.displayName;

const AccordionItem = forwardRef<AccordionItemRef, AccordionItemPrimitiveProps>(
  ({ className, children, testID, value, disabled, ...injected }, ref) => {
    useWarnIfEmptyItemValue(value);
    const root = useContext(AccordionRootContext);
    // Like Radix, an empty value never counts as open
    const isExpanded =
      value !== "" &&
      (root?.type === "multiple" ? root.value.includes(value) : root?.value === value);
    return (
      <AccordionItemContext.Provider value={{ value, disabled, isExpanded }}>
        <AccordionPrimitive.Item
          ref={ref}
          value={value}
          disabled={disabled}
          className={className}
          {...platformProps({ testID, slot: "accordion-item" })}
          {...injected}
        >
          {children}
        </AccordionPrimitive.Item>
      </AccordionItemContext.Provider>
    );
  },
);

AccordionItem.displayName = AccordionPrimitive.Item.displayName;

const AccordionHeader = forwardRef<AccordionHeaderRef, AccordionHeaderPrimitiveProps>(
  ({ className, children, testID, ...injected }, ref) => (
    <AccordionPrimitive.Header
      ref={ref}
      className={className}
      {...platformProps({ testID, slot: "accordion-header" })}
      {...injected}
    >
      {children}
    </AccordionPrimitive.Header>
  ),
);

AccordionHeader.displayName = AccordionPrimitive.Header.displayName;

const AccordionTrigger = forwardRef<AccordionTriggerRef, AccordionTriggerPrimitiveProps>(
  ({ className, children, testID, disabled, ...injected }, ref) => {
    // One rule on every platform: Radix would let the trigger's own `false`
    // enable it under a disabled item or root
    const resolved = useTriggerDisabled(disabled);
    return (
      <AccordionPrimitive.Trigger
        ref={ref}
        disabled={resolved}
        className={className}
        {...platformProps({ testID, slot: "accordion-trigger" })}
        {...injected}
      >
        {children}
      </AccordionPrimitive.Trigger>
    );
  },
);

AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = forwardRef<AccordionContentRef, AccordionContentPrimitiveProps>(
  ({ className, children, testID, forceMount, ...injected }, ref) => (
    <AccordionPrimitive.Content
      ref={ref}
      forceMount={forceMount}
      // Radix shows forced content while its item is closed: it stays mounted,
      // but hidden from sight and screen readers, as on native. Important,
      // over a caller's display class
      className={cn(forceMount && "data-[state=closed]:!hidden", className)}
      {...platformProps({ testID, slot: "accordion-content" })}
      {...injected}
    >
      {children}
    </AccordionPrimitive.Content>
  ),
);

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { AccordionRoot, AccordionItem, AccordionHeader, AccordionTrigger, AccordionContent };
